import { combineStrings } from 'assets/utils/data/String';
import { values } from 'lodash';
import MediaFile from 'models/core/mediaFile/Model.MediaFile';
import User from 'models/core/user/Model.User';
import RefundPaymentType from 'models/enums/RefundPaymentType';
import Model from 'models/Model';
import Product from 'models/productManagement/product/Model.Product';
import ProductEnhancement from 'models/productManagement/productEnhancement/Model.ProductEnhancement';
import Provider from 'models/productManagement/provider/Model.Provider';
import ContractRefundPayee from 'models/sales/contractRefundPayee/Model.ContractRefundPayee';
import Distributor from 'models/sales/distributor/Model.Distributor';
import ClaimTypeDealerAutomationRule from '../claimTypeDealerAutomationRule/Model.ClaimTypeDealerAutomationRule';
import DealerClaimType from '../dealerClaimType/Model.DealerClaimType';
import DealerGroup from '../dealerGroup/Model.DealerGroup';
import ProductDealerDocument from '../productDealerDocument/Model.ProductDealerDocument';
import ProductEnhancementDealerDocument from '../productEnhancementDealerDocument/Model.ProductEnhancementDealerDocument';
import dealerDeleteApi from './delete/Api.Dealer.Delete';
import dealerFindApi from './find/Api.Dealer.Find';
import dealerListApi from './list/Api.Dealer.List';
import dealerListPublicApi from './listPublic/Api.Dealer.ListPublic';
import dealerSaveApi from './save/Api.Dealer.Save';

export default class Dealer
  extends Model<Model.IDealer>({ icon: 'if-login' })
  implements Model.Instance<Model.IDealer>
{
  id: number;
  name?: string;
  isInactive: boolean = false;
  isAutomaticStatementEnabled: boolean = false;
  isAutomaticStatementBalanceEnabled: boolean = false;
  isAutomaticPreliminaryStatementEnabled: boolean = false;
  isPaymentRequired: boolean = false;
  nonUserStatementEmailEnabled: boolean;
  nonUserStatementBalanceEmailEnabled: boolean;
  primaryPhoneNumber?: string;
  readonly phoneNumber?: string;
  primaryEmailAddress?: string;
  readonly email?: string;
  contractConsentProductAggregates?: Model.IContractConsentProductAggregate[];
  accountingEmails?: string[];
  claimPaymentEmail?: string;
  representativeId: number;
  readonly distributorId?: number;
  representative: Distributor;
  readonly providerId: number;
  provider: Provider;
  dealerGroupId?: number;
  dealerGroup: DealerGroup;
  mediaFiles?: MediaFile[];
  streetAddress?: string;
  city?: string;
  provinceCode?: string;
  countryCode?: string;
  postalCode?: string;
  federalTaxRegistrationNumber?: string;
  provincialTaxRegistrationNumber?: string;
  isFederalTaxExempt: boolean = false;
  isProvincialTaxExempt: boolean = false;
  isApprovedByUnderwriter: boolean = false;
  users?: User[];
  products?: Product[];
  productEnhancements?: ProductEnhancement[];
  uiSettingsFile?: MediaFile;
  uiSettings?: Utils.UiSettings;
  productDealerDocuments?: Array<ProductDealerDocument>;
  productEnhancementDealerDocuments?: Array<ProductEnhancementDealerDocument>;
  labourRate?: number;
  paymentMethodCode?: string;
  documentLanguageIsoCode?: Locale.Languages;

  mainDistributorId?: number;
  mainDistributor?: User;

  //#region Veraforte
  vertaforeSubBrokerNumber?: string;
  //#endregion

  //#region Automated ClaimTypeJobItems
  claimTypeDealerAutomationRules: ClaimTypeDealerAutomationRule[];
  dealerClaimTypes: DealerClaimType[];
  //#endregion

  constructor(data: Partial<Model.IDealer> | Utils.FormData<Model.IDealer>, language?: Locale.Languages) {
    super(
      {
        representative: Distributor,
        provider: Provider,
        dealerGroup: DealerGroup,
        uiSettingsFile: MediaFile,
        mediaFiles: [MediaFile],
        users: [User],
        products: [Product],
        productEnhancements: [ProductEnhancement],
        productDealerDocuments: [ProductDealerDocument],
        productEnhancementDealerDocuments: [ProductEnhancementDealerDocument],
        claimTypeDealerAutomationRules: [ClaimTypeDealerAutomationRule],
        dealerClaimTypes: [DealerClaimType],
        mainDistributor: User,
      },
      language
    );
    this.update(data);
  }
  get displayInfo(): Model.DisplayInfo {
    return {
      id: this.id,
      title: this.name || this.id?.toString(),
      info: combineStrings(' | ', this.representative?.name, this.dealerGroup?.name),
      subtitle: this.products
        ?.filter((p) => p.isActive)
        ?.map((p) => p.name)
        ?.join(', '),
    };
  }

  get fullAddress() {
    return combineStrings(', ', this.streetAddress, combineStrings(' ', this.postalCode, this.provinceCode));
  }
  toContractRefundPayee(): ContractRefundPayee {
    return new ContractRefundPayee({
      dealerId: this.id,
      dealer: this,
      isPrimary: true,
      paymentMethodCode: (values(RefundPaymentType) as string[]).includes(this.paymentMethodCode)
        ? this.paymentMethodCode
        : undefined,
    });
  }

  static async list(body?: Api.Core.Dealer.List.IRequest) {
    const { payload, ...rest } = await dealerListApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new Dealer(it)),
        count: payload.count,
      },
    };
  }

  static async listPublic(req: Api.Core.Dealer.ListPublic.IRequest) {
    const { payload, ...rest } = await dealerListPublicApi(req);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new Dealer(it)),
        count: payload.count,
      },
    };
  }

  static async find(params: { id: number }) {
    const { payload, ...rest } = await dealerFindApi(params);
    return {
      ...rest,
      payload: new Dealer(payload),
    };
  }
  async save() {
    return await dealerSaveApi(this);
  }
  async delete() {
    return await dealerDeleteApi({ id: this.id });
  }
}
